import {
  ArrowRight,
  FileStack,
  GanttChartSquare,
  Layers,
  Puzzle,
  Sparkles,
  Target,
  Users,
  Warehouse,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import { LocaleLink } from "./LocaleLink";
import { Reveal } from "./Reveal";
import { ISO, addonIso } from "@/content/illustrations";
import type { PricingAddon } from "@/lib/pricing.types";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n-dict";
import { SHOW_PRICES } from "@/lib/pricing-visibility";


const ICONS: Record<string, LucideIcon> = {
  target: Target,
  users: Users,
  layers: Layers,
  "file-stack": FileStack,
  sparkles: Sparkles,
  gantt: GanttChartSquare,
  wrench: Wrench,
  warehouse: Warehouse,
};

export function addonIcon(name: string): LucideIcon {
  return ICONS[name] ?? Puzzle;
}

function PlanChips({ planNames }: { planNames: string[] }) {
  const t = useT();
  if (!planNames.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground">{t("Available for")}</span>
      {planNames.map((name) => (
        <span
          key={name}
          className="rounded-full border border-border px-2.5 py-0.5 text-xs leading-relaxed tracking-wide text-muted-foreground"
        >
          {name}
        </span>
      ))}
    </div>
  );
}

function Price({ addon, onRequestLabel }: { addon: PricingAddon; onRequestLabel: string }) {
  return (
    <div>
      <span className="font-display text-2xl font-bold">
        {SHOW_PRICES ? addon.price : onRequestLabel}
      </span>
      {SHOW_PRICES && addon.period ? <span className="text-sm text-muted-foreground"> {addon.period}</span> : null}
    </div>
  );
}

/** Rich add-on card used on the Pricing page and the /addons hub. */
export function AddonCard({
  addon,
  planNames,
  delay = 0,
  detailed = true,
  stepsPreview = false,
  wide = false,
}: {
  addon: PricingAddon;
  planNames: string[];
  delay?: number;
  detailed?: boolean;
  /** Show a compact numbered "How it works" preview from the add-on's steps. */
  stepsPreview?: boolean;
  /** Wide horizontal layout for vertical stacks (image left, content right). */
  wide?: boolean;
}) {
  const t = useT();
  const Icon = addonIcon(addon.icon);
  const bullets = detailed ? addon.highlights.slice(0, 6) : [];

  if (wide) {
    return (
      <Reveal
        delay={delay}
        className={cn(
          "grid overflow-hidden rounded-xl border transition-colors md:grid-cols-[minmax(0,320px)_minmax(0,1fr)]",
          addon.featured
            ? "border-primary/40 bg-surface hover:border-primary/60"
            : "border-border bg-surface/40 hover:border-border/80",
        )}
      >
        <div className="relative h-44 border-b border-border/60 bg-[var(--iso-paper,#f1f2ec)] md:h-auto md:border-b-0 md:border-r">
          <img
            src={ISO[addonIso(addon.slug)]}
            alt=""
            aria-hidden
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 hover:scale-[1.03]"
          />
        </div>
        <div className="flex min-w-0 flex-col p-6 md:p-8">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-1">
            <Icon className="size-6 self-center text-primary" aria-hidden />
            <h3 className="font-display text-xl font-bold md:text-2xl">{addon.name}</h3>
            {addon.tagline ? (
              <p className="text-sm font-medium text-primary-ink">{addon.tagline}</p>
            ) : null}
          </div>
          <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
            {addon.description}
          </p>

          {bullets.length ? (
            <ul className="mt-5 grid gap-2 sm:grid-cols-2">
              {bullets.map((b) => (
                <li key={b} className="flex gap-2.5 text-sm text-muted-foreground">
                  <span
                    aria-hidden
                    className="mt-2 size-1.5 shrink-0 rounded-full bg-primary/70"
                  />
                  {b}
                </li>
              ))}
            </ul>
          ) : null}

          {stepsPreview && addon.steps.length ? (
            <div className="mt-6 flex-1 rounded-lg border border-border/60 bg-background/40 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] leading-relaxed text-muted-foreground">
                {t("How it works")}
              </p>
              <ol className="mt-3 grid gap-2.5 sm:grid-cols-3">
                {addon.steps.slice(0, 3).map((step, i) => (
                  <li key={step.id} className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="flex size-5 shrink-0 items-center justify-center rounded bg-primary/10 text-[11px] font-bold leading-none text-primary-ink"
                    >
                      {i + 1}
                    </span>
                    <span className="text-xs leading-snug text-muted-foreground md:text-sm">
                      {step.title}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <div className="flex-1" />
          )}

          <div className="mt-6 flex min-w-0 flex-wrap items-center justify-between gap-x-6 gap-y-4">
            <Price addon={addon} onRequestLabel={t("On request")} />
            <PlanChips planNames={planNames} />
            <LocaleLink
              to={`/addons/${addon.slug}`}
              className="ml-auto inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary-ink transition-opacity hover:opacity-80"
            >
              {t("Explore")} {addon.name}
              <ArrowRight className="size-4" aria-hidden />
            </LocaleLink>
          </div>
        </div>
      </Reveal>
    );
  }

  return (
    <Reveal
      delay={delay}
      className={cn(
        "flex h-full flex-col rounded-xl border p-6 transition-colors",
        addon.featured
          ? "border-primary/40 bg-surface hover:border-primary/60"
          : "border-border bg-surface/40 hover:border-border/80",
      )}
    >
      <div className="mb-5 overflow-hidden rounded-lg border border-border/60 bg-[var(--iso-paper,#f1f2ec)]">
        <img
          src={ISO[addonIso(addon.slug)]}
          alt=""
          aria-hidden
          loading="lazy"
          className="h-36 w-full object-cover object-center transition-transform duration-500 hover:scale-[1.03]"
        />
      </div>
      <Icon className="size-6 text-primary" aria-hidden />
      <h3 className="mt-4 font-display text-lg font-bold">{addon.name}</h3>

      {addon.tagline ? (
        <p className="mt-1 text-sm font-medium text-primary-ink">{addon.tagline}</p>
      ) : null}
      <p className="mt-3 text-sm text-muted-foreground md:text-base">{addon.description}</p>

      {bullets.length ? (
        <ul className="mt-5 space-y-2">
          {bullets.map((b) => (
            <li key={b} className="flex gap-2.5 text-sm text-muted-foreground">
              <span
                aria-hidden
                className="mt-2 size-1.5 shrink-0 rounded-full bg-primary/70"
              />
              {b}
            </li>
          ))}
        </ul>
      ) : null}

      {stepsPreview && addon.steps.length ? (
        <div className="mt-5 flex-1 rounded-lg border border-border/60 bg-background/40 p-3.5">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] leading-relaxed text-muted-foreground">
            {t("How it works")}
          </p>
          <ol className="mt-2.5 space-y-2">
            {addon.steps.slice(0, 3).map((step, i) => (
              <li key={step.id} className="flex items-center gap-2">
                <span
                  aria-hidden
                  className="flex size-5 shrink-0 items-center justify-center rounded bg-primary/10 text-[11px] font-bold leading-none text-primary-ink"
                >
                  {i + 1}
                </span>
                <span className="text-xs leading-snug text-muted-foreground md:text-sm">
                  {step.title}
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      <div className="mt-6">
        <Price addon={addon} onRequestLabel={t("On request")} />
      </div>

      <div className="mt-4">
        <PlanChips planNames={planNames} />
      </div>

      <LocaleLink
        to={`/addons/${addon.slug}`}
        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-ink transition-opacity hover:opacity-80"
      >
        {t("Explore")} {addon.name}
        <ArrowRight className="size-4" aria-hidden />
      </LocaleLink>
    </Reveal>
  );
}
